import * as React from "react";
import { type Quota } from "@muscat/types";
import { Table } from "react-bootstrap";
import { quotaCode } from "../../../../../server/lib/concept/v3/static";

const QuotaCodeReg = {
	test1: new RegExp(`^${quotaCode.test1}\\d+$`),
	test2: new RegExp(`^${quotaCode.test2}\\d+$`),
	currentProduct: new RegExp(`^${quotaCode.currentProduct}\\d+$`),
	category: new RegExp(`^${quotaCode.category}\\d+$`),
	strategy: new RegExp(`^${quotaCode.strategy}\\d+$`),
	brandUser: new RegExp(`^${quotaCode.brandUser}\\d+$`),
} as const;

type QuotaCategoryProps = {
	title: string;
	isCMI?: boolean;
	quotas: Quota[];
};
const QuotaCategory = React.memo((props: QuotaCategoryProps) => {
	const { title, quotas, isCMI } = props;
	const planTotal = React.useMemo(() => {
		return quotas.reduce((a, b) => a + b.plan, 0);
	}, [quotas]);
	const countTotal = React.useMemo(() => {
		return quotas.reduce((a, b) => a + b.count, 0);
	}, [quotas]);
	if (quotas.length === 0) return <></>;
	return (
		<>
			<tr>
				<th style={{ backgroundColor: "#ddd" }} colSpan={5}>
					{title}
				</th>
			</tr>
			{isCMI &&
				quotas.map((quota) => {
					const rate = Math.floor((quota.count / quota.plan) * 1000) / 10;
					return (
						<tr key={`quota-${quota.code}`}>
							<td>{quota.code}</td>
							<td>{quota.name}</td>
							<td>{quota.plan}</td>
							<td>{quota.count}</td>
							<td style={{ color: rate >= 100 ? "blue" : "red" }}>{rate}</td>
						</tr>
					);
				})}
			<tr>
				<td colSpan={2} style={{ textAlign: "center" }}>
					合計
				</td>
				<td>{planTotal}</td>
				<td>{countTotal}</td>
				<td>{Math.floor((countTotal / planTotal) * 1000) / 10}</td>
			</tr>
		</>
	);
});

export type QuotaStatusV3Props = {
	isCMI?: boolean;
	quotas: Quota[];
	/**
	 * REP回収の有無
	 */
	isDemandForecastNeeded?: boolean;
};

type CategorizedQuotas = {
	test1: Quota[];
	test2: Quota[];
	currentProduct: Quota[];
	categoryTargets: Quota[];
	strangicTargets: Quota[];
	brandUserTargets: Quota[];
};

const categoryLabels = {
	test1: "テスト品1 一般サンプル",
	test2: "テスト品2 一般サンプル",
	currentProduct: "現行品 一般サンプル",
	categoryTargets: "カテゴリターゲットグループ",
	strangicTargets: "戦略ターゲットグループ",
	brandUserTargets: "当該カテゴリ×ブランドユーザーグループ",
} satisfies { [key in keyof CategorizedQuotas]: string };

export const QuotaStatusV3 = React.memo((props: QuotaStatusV3Props) => {
	const { quotas, isCMI, isDemandForecastNeeded = false } = props;
	const categorizedQuotas = React.useMemo(() => {
		const tmp: CategorizedQuotas = {
			test1: [],
			test2: [],
			currentProduct: [],
			categoryTargets: [],
			strangicTargets: [],
			brandUserTargets: [],
		};
		quotas.forEach((q) => {
			if (isDemandForecastNeeded && QuotaCodeReg.test1.test(q.code)) {
				tmp.test1.push(q);
			}
			if (isDemandForecastNeeded && QuotaCodeReg.test2.test(q.code)) {
				tmp.test2.push(q);
			}
			if (isDemandForecastNeeded && QuotaCodeReg.currentProduct.test(q.code)) {
				tmp.currentProduct.push(q);
			}
			if (QuotaCodeReg.category.test(q.code)) {
				tmp.categoryTargets.push(q);
			}
			if (QuotaCodeReg.strategy.test(q.code)) {
				tmp.strangicTargets.push(q);
			}
			if (QuotaCodeReg.brandUser.test(q.code)) {
				tmp.brandUserTargets.push(q);
			}
		});
		return tmp;
	}, [isDemandForecastNeeded, quotas]);

	return (
		<Table>
			<thead>
				<tr>
					<th>No</th>
					<th>名前</th>
					<th>回収予定数</th>
					<th>回収数</th>
					<th>回収率</th>
				</tr>
			</thead>
			<tbody>
				{Object.entries(categorizedQuotas).map(([group, quotas]: [keyof CategorizedQuotas, Quota[]]) => {
					return (
						<QuotaCategory key={`quota-group-${group}`} isCMI={isCMI} title={categoryLabels[group]} quotas={quotas} />
					);
				})}
			</tbody>
		</Table>
	);
});
